import {useEffect, useLayoutEffect} from 'react';

export const isBrowser = typeof window !== 'undefined';

/**
 * # useIsomorphicLayoutEffect
 *
 * A React helper hook for scheduling a [layout effect](https://reactjs.org/docs/hooks-reference.html#uselayouteffect) with a fallback to a [regular effect](https://reactjs.org/docs/hooks-reference.html#useeffect) for environments where layout effects should not be used (such as server-side rendering).
 *
 * ## Usage
 *
 * The follow example showcases how to **reliably** retrieve the closest `document.body` of the component, which is a technique required for components rendering within iFrames.
 *
 * ```jsx
 * import React from 'react';
 * import {useIsomorphicLayoutEffect} from '@packages/systems/design-system/utils';
 *
 * function BaseComponent(props) {
 *   const nodeRef = React.useRef(null);
 *   const [body, setBody] = React.useState(null);
 *
 *   useIsomorphicLayoutEffect(() => {
 *     setBody(nodeRef.current.ownerDocument.body);
 *   }, []);
 *
 *   useEffect(
 *     () => {
 *       if (body) {
 *         // Do something with document.body
 *       }
 *     },
 *     [body]
 *   );
 *
 *   return <div ref={nodeRef} {...props} />;
 * }
 * ```
 */
export const useIsomorphicLayoutEffect = isBrowser
  ? useLayoutEffect
  : useEffect;
